/* One photo/eye draft. The injected adapter owns authentication, transport and safe image URLs. */
(() => {
  'use strict';

  function createController(options = {}) {
    const adapter = options.adapter;
    if (!adapter || ['begin', 'read', 'recover', 'finalize'].some(key => typeof adapter[key] !== 'function')) {
      throw new Error('A character draft adapter is required.');
    }
    const makeId = options.makeId || (() => globalThis.crypto.randomUUID());
    const later = options.setTimeout || globalThis.setTimeout;
    const clear = options.clearTimeout || globalThis.clearTimeout;
    const now = options.now || Date.now;
    const maxPollAttempts = options.maxPollAttempts ?? 60;
    const listeners = new Set();
    let input = null, finalInput = null, timer = null, epoch = 0, nextAt = 0, pollDelay = 2000;
    let startFlight = null, readFlight = null, finalFlight = null, retryFlight = null;
    let state = {
      status: 'idle', draftId: null, requestKey: null, metadata: { name: '', age: null, gender: '' },
      portrait: null, error: null, retryable: false, paused: false, polling: false, pollAttempts: 0,
      finalization: { status: 'idle', requestKey: null, result: null, error: null }
    };
    const fail = (code, message) => Object.assign(new Error(message), { code });
    const freeze = value => {
      if (value == null || typeof value !== 'object') return value;
      if (Array.isArray(value)) return Object.freeze(value.map(freeze));
      return Object.freeze(Object.fromEntries(Object.entries(value).map(([key, item]) => [key, freeze(item)])));
    };
    const getState = () => freeze(state);
    const emit = patch => {
      state = { ...state, ...patch };
      const snapshot = getState();
      listeners.forEach(listener => { try { listener(snapshot); } catch (_) { /* A view cannot interrupt recovery. */ } });
      return snapshot;
    };
    const errorInfo = (error, fallback) => Object.freeze({ code: error?.code || fallback,
      message: error?.message || 'We could not confirm this step. Check it again before continuing.' });
    const active = token => token === epoch && state.status !== 'cancelled';
    const uncertainError = error => error?.uncertain === true || (error?.admissionRejected !== true && error?.definitive !== true);
    const watchable = () => Boolean(state.draftId) && ['processing', 'uncertain'].includes(state.status);
    const stopTimer = () => { if (timer !== null) clear(timer); timer = null; };
    function schedule() {
      stopTimer();
      if (state.paused || !watchable() || readFlight) return;
      if (state.pollAttempts >= maxPollAttempts) {
        emit({ polling: false, retryable: true, error: { code: 'POLLING_PAUSED',
          message: 'Your character is still being checked. Check again when you are ready.' } });
        return;
      }
      timer = later(() => { timer = null; readDraft().catch(() => {}); }, Math.max(0, nextAt - now()));
    }
    function draftResult(value) {
      if (!value || typeof value.draftId !== 'string' || !value.draftId ||
          !['processing', 'ready', 'failed', 'uncertain', 'expired'].includes(value.status)) {
        throw fail('DRAFT_RESPONSE_INVALID', 'We could not confirm your character. Check this request again.');
      }
      if (state.draftId && state.draftId !== value.draftId) {
        throw fail('DRAFT_RESPONSE_CHANGED', 'The character response did not match this photo.');
      }
      if (value.status === 'ready' && (!value.portrait?.url || !/^(https:\/\/|blob:)/i.test(value.portrait.url))) {
        throw fail('DRAFT_PORTRAIT_UNAVAILABLE', 'Your character image is not available yet. Check again.');
      }
      pollDelay = Number.isFinite(value.retryAfterMs) && value.retryAfterMs > 0 ? Math.max(2000, value.retryAfterMs) : 2000;
      nextAt = now() + pollDelay;
      const terminalFailure = ['failed', 'expired'].includes(value.status);
      emit({ draftId: value.draftId, status: value.status, portrait: value.status === 'ready' ? freeze(value.portrait) : null,
        error: terminalFailure ? errorInfo(value.error, value.status === 'expired' ? 'DRAFT_EXPIRED' : 'PORTRAIT_FAILED') : null,
        retryable: false, polling: false });
      schedule();
      return getState();
    }
    function startError(error) {
      emit({ status: uncertainError(error) ? 'uncertain' : 'failed', error: errorInfo(error, 'DRAFT_NOT_CONFIRMED'),
        retryable: error?.retryable !== false, polling: false });
    }
    function beginOriginal() {
      const token = epoch, original = input;
      emit({ status: 'starting', error: null, retryable: false });
      const flight = Promise.resolve().then(() => active(token) ? adapter.begin(original) : null).then(value => {
        if (active(token)) draftResult(value);
        return getState();
      }).catch(error => {
        if (active(token)) startError(error);
        throw error;
      }).finally(() => { if (startFlight === flight) startFlight = null; });
      startFlight = flight;
      return flight;
    }
    function start(details) {
      if (state.status === 'cancelled') return Promise.reject(fail('DRAFT_CANCELLED', 'This character draft is closed.'));
      if (!details?.photo || !['child', 'adult'].includes(details.personType) || typeof details.role !== 'string' ||
          typeof (details.eyeColor ?? '') !== 'string' || details.permission?.accepted !== true) {
        return Promise.reject(fail('INVALID_DRAFT_INPUT', 'Choose a photo and confirm its eye color first.'));
      }
      const candidate = { requestKey: details.requestKey || input?.requestKey || makeId(), photo: details.photo,
        role: details.role, personType: details.personType, eyeColor: details.eyeColor || '', permission: freeze(details.permission) };
      if (input) {
        const same = ['requestKey', 'photo', 'role', 'personType', 'eyeColor'].every(key => candidate[key] === input[key]) &&
          JSON.stringify(candidate.permission) === JSON.stringify(input.permission);
        if (!same) return Promise.reject(fail('DRAFT_INPUT_CHANGED', 'A different photo or eye color needs a new character draft.'));
        // Reopening a form does not authorize another POST, including after a lost response.
        return startFlight || Promise.resolve(getState());
      }
      input = Object.freeze(candidate);
      emit({ requestKey: input.requestKey });
      return beginOriginal();
    }
    function setMetadata(patch) {
      if (state.status === 'cancelled') throw fail('DRAFT_CANCELLED', 'This character draft is closed.');
      if (finalInput) throw fail('DRAFT_METADATA_FROZEN', 'These details are saved with the current request.');
      const metadata = { ...state.metadata };
      for (const key of Object.keys(patch || {})) {
        const value = patch[key];
        if (key === 'name' && typeof value === 'string' && value.length <= 100) metadata.name = value;
        else if (key === 'gender' && typeof value === 'string' && value.length <= 40) metadata.gender = value;
        else if (key === 'age' && (value === null || Number.isInteger(value) && value >= 0 && value <= 120)) metadata.age = value;
        else throw fail('INVALID_DRAFT_METADATA', 'Check the character name, age and gender.');
      }
      return emit({ metadata });
    }
    function readDraft() {
      if (readFlight) return readFlight;
      if (!state.draftId || state.status === 'cancelled') return Promise.resolve(getState());
      const token = epoch, draftId = state.draftId;
      emit({ polling: true, pollAttempts: state.pollAttempts + 1 });
      const flight = Promise.resolve().then(() => active(token) ? adapter.read(draftId) : null).then(value => {
        if (active(token)) draftResult(value);
        return getState();
      }).catch(error => {
        if (active(token)) emit({ polling: false, error: errorInfo(error, 'DRAFT_READ_FAILED'), retryable: true });
        throw error;
      }).finally(() => {
        if (readFlight === flight) readFlight = null;
        if (active(token) && !state.error) schedule();
      });
      readFlight = flight;
      return flight;
    }
    const confirmedSave = value => value?.registered === true || typeof value?.profileId === 'string' && Boolean(value.profileId);
    const savedResult = value => {
      const result = value?.result || value;
      if (!confirmedSave(result)) throw fail('DRAFT_FINALIZE_UNCONFIRMED', 'We could not confirm the saved character. Check this request again.');
      emit({ finalization: { status: 'saved', requestKey: finalInput.requestKey, result: freeze(result), error: null } });
      return getState();
    };
    function finalizeOriginal() {
      const token = epoch, draftId = state.draftId, original = finalInput;
      emit({ finalization: { status: 'saving', requestKey: finalInput.requestKey, result: null, error: null } });
      const flight = Promise.resolve().then(() => active(token) ? adapter.finalize(draftId, original) : null).then(value => {
        if (active(token)) savedResult(value);
        return getState();
      }).catch(error => {
        if (active(token)) emit({ finalization: { status: uncertainError(error) ? 'uncertain' : 'failed',
          requestKey: finalInput.requestKey, result: null, error: errorInfo(error, 'DRAFT_FINALIZE_UNCONFIRMED') } });
        throw error;
      }).finally(() => { if (finalFlight === flight) finalFlight = null; });
      finalFlight = flight;
      return flight;
    }
    function finalize(settings = {}) {
      if (state.status === 'cancelled') return Promise.reject(fail('DRAFT_CANCELLED', 'This character draft is closed.'));
      if (finalFlight) return finalFlight;
      if (state.finalization.status === 'saved') return Promise.resolve(getState());
      if (finalInput) return Promise.reject(fail('DRAFT_RETRY_REQUIRED', 'Check the existing save request before retrying.'));
      if (!state.draftId) return Promise.reject(fail('DRAFT_NOT_ADMITTED', 'Wait for your photo to be accepted first.'));
      if (state.status === 'expired') return Promise.reject(fail('DRAFT_EXPIRED', 'Choose the photo again to create a new character.'));
      const name = state.metadata.name.trim();
      if (!name) return Promise.reject(fail('CHARACTER_NAME_REQUIRED', 'Enter the character name.'));
      finalInput = freeze({ requestKey: settings.requestKey || makeId(), name, storyMetadata: {
        ...(state.metadata.age !== null ? { age: state.metadata.age } : {}),
        ...(state.metadata.gender.trim() ? { gender: state.metadata.gender.trim() } : {}) } });
      return finalizeOriginal();
    }
    function retry() {
      if (retryFlight) return retryFlight;
      if (state.status === 'cancelled') return Promise.reject(fail('DRAFT_CANCELLED', 'This character draft is closed.'));
      if (startFlight || finalFlight) return startFlight || finalFlight;
      if (!input) return Promise.reject(fail('DRAFT_NOT_STARTED', 'Choose a photo first.'));
      const token = epoch;
      const retryFinal = finalInput && state.finalization.status !== 'saved';
      const key = retryFinal ? finalInput.requestKey : input.requestKey;
      const flight = Promise.resolve().then(async () => {
        if (!active(token)) return getState();
        if (!retryFinal && state.draftId) {
          stopTimer(); emit({ pollAttempts: 0, error: null });
          return readDraft();
        }
        const receipt = await adapter.recover(key);
        if (!active(token)) return getState();
        if (receipt?.status === 'not-admitted') return retryFinal ? finalizeOriginal() : beginOriginal();
        if (receipt?.status === 'rejected') {
          const error = errorInfo(receipt.error, 'DRAFT_REQUEST_REJECTED');
          if (retryFinal) emit({ finalization: { ...state.finalization, status: 'failed', error } });
          else emit({ status: 'failed', error, retryable: false });
          return getState();
        }
        if (retryFinal) {
          if (confirmedSave(receipt) || confirmedSave(receipt?.result)) return savedResult(receipt);
          emit({ finalization: { ...state.finalization, status: 'uncertain', error: {
            code: 'DRAFT_FINALIZE_PENDING', message: 'Your save request is still being checked. Check again shortly.' } } });
          return getState();
        }
        if (receipt?.draftId) return draftResult(receipt);
        emit({ status: 'uncertain', error: { code: 'DRAFT_REQUEST_PENDING',
          message: 'Your photo request is still being checked. Check again shortly.' }, retryable: true });
        return getState();
      }).catch(error => {
        if (active(token)) {
          if (retryFinal) emit({ finalization: { ...state.finalization, status: 'uncertain', error: errorInfo(error, 'DRAFT_RECOVERY_FAILED') } });
          else startError(error);
        }
        throw error;
      }).finally(() => { if (retryFlight === flight) retryFlight = null; });
      retryFlight = flight;
      return flight;
    }
    function pause() { stopTimer(); return emit({ paused: true }); }
    function resume() {
      if (state.status === 'cancelled') return getState();
      emit({ paused: false }); schedule(); return getState();
    }
    function cancel() {
      epoch++; stopTimer(); input = null; finalInput = null;
      adapter.dispose?.();
      return emit({ status: 'cancelled', draftId: null, requestKey: null, portrait: null, metadata: { name: '', age: null, gender: '' },
        error: null, retryable: false, polling: false, paused: true,
        finalization: { status: 'idle', requestKey: null, result: null, error: null } });
    }
    return Object.freeze({ start, setMetadata, finalize, retry, pause, resume, cancel, getState,
      subscribe(listener) { listeners.add(listener); listener(getState()); return () => listeners.delete(listener); } });
  }
  const api = Object.freeze({ createController });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.WonderbookCharacterDraft = api;
})();
